import { Link, useLocation } from '@tanstack/react-router'
import { Separator } from '@/components/ui/separator'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { AppSidebar } from './app-sidebar'
import { ModeToggle } from '@/components/mode-toggle'
import { CommandPalette, CommandPaletteTrigger } from '@/components/shared/command-palette'
import { BreadcrumbOverridesProvider } from './breadcrumb-overrides'
import { useOverrides } from '@/hooks/use-breadcrumb-overrides'
import { HeaderUser } from './header-user'
import { RafobaseLogo } from '@/components/rafobase-logo'

type AppLayoutProps = {
  children: React.ReactNode
}

// ─── Breadcrumb label mapping ────────────────────────────────────────────────
const BREADCRUMB_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  analytics: 'Analytics',
  members: 'Membros',
  referrals: 'Indicações',
  network: 'Rede',
  creators: 'Creators',
  campaigns: 'Programa',
  'social-listening': 'Social Listening',
  settings: 'Configurações',
  general: 'Geral',
  integrations: 'Integrações',
  tiny: 'Tiny ERP',
  yampi: 'Yampi',
  guru: 'Guru',
  klaviyo: 'Klaviyo',
  notifications: 'Notificações',
  team: 'Equipe',
  playground: 'Playground',
  history: 'Histórico',
  developers: 'Desenvolvedor',
  webhooks: 'Webhooks',
  incoming: 'Entrada',
  outbound: 'Saída',
  backoffice: 'Backoffice',
  merchants: 'Lojistas',
}

function Breadcrumbs() {
  const location = useLocation()
  const overrides = useOverrides()
  const segments = location.pathname.split('/').filter(Boolean)

  if (segments.length === 0) return null

  // Build breadcrumb items with paths
  const crumbs = segments.map((segment, i) => ({
    label: BREADCRUMB_LABELS[segment] || overrides[segment] || segment,
    path: '/' + segments.slice(0, i + 1).join('/'),
    isLast: i === segments.length - 1,
  }))

  return (
    <nav className="flex items-center gap-1.5" aria-label="Breadcrumb">
      {crumbs.map((crumb, i) => (
        <span key={crumb.path} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-[13px] text-muted-foreground/40 select-none">/</span>}
          {crumb.isLast ? (
            <span className="text-[13px] font-medium text-foreground">{crumb.label}</span>
          ) : (
            <Link
              to={crumb.path}
              className="text-[13px] text-muted-foreground hover:text-foreground transition-colors"
            >
              {crumb.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  )
}

export function AppLayout({ children }: AppLayoutProps) {
  return (
    <BreadcrumbOverridesProvider>
      <CommandPalette>
        <div className="flex flex-col h-svh">
          {/* Global header — full width, above sidebar+content */}
          <header className="flex h-12 shrink-0 items-center gap-2 px-4 bg-background md:bg-sidebar border-b border-border/40 md:border-b-0 relative z-40">
            <div className="flex items-center gap-2 min-w-0">
              <Link to="/">
                <RafobaseLogo className="h-5 w-auto text-foreground" />
              </Link>
            </div>
            <div className="hidden md:flex flex-1 items-center justify-center">
              <CommandPaletteTrigger />
            </div>
            <div className="flex items-center justify-end gap-1.5 ml-auto md:ml-0">
              <ModeToggle />
              <HeaderUser />
            </div>
          </header>

          {/* Sidebar + Content — fills remaining height */}
          <SidebarProvider className="flex-1 min-h-0 bg-sidebar **:data-[slot=sidebar-container]:top-12 **:data-[slot=sidebar-container]:h-[calc(100svh-3rem)]">
            <AppSidebar />
            <SidebarInset className="min-w-0 overflow-hidden">
              {/* Content sub-header — trigger + breadcrumbs */}
              <div className="flex items-center gap-2 h-10 shrink-0 px-4 md:px-6 border-b border-border/60">
                <SidebarTrigger className="-ml-1" />
                <Separator orientation="vertical" className="h-4" />
                <Breadcrumbs />
              </div>
              <div className="flex-1 min-w-0 p-4 md:p-6 overflow-y-auto animate-in fade-in duration-300">
                {children}
              </div>
            </SidebarInset>
          </SidebarProvider>
        </div>
      </CommandPalette>
    </BreadcrumbOverridesProvider>
  )
}
